<template>
  <div :class="['m-vue-select-container', {'m-vue-select--disabled': isDisabled}]">
    <span
      v-if="label"
      class="m-vue-select-label"
      v-html="label"
    />
    <div
      class="m-vue-select"
      ref="vSelectContainer"
    >
      <vSelect
        :model-value="value"
        :reduce="reduce"
        :options="options"
        :clearable="false"
        :searchable="isSearchable"
        :filterable="false"
        :placeholder="placeholder"
        @search="onSearch"
        @update:modelValue="onInput"
        append-to-body
        :calculate-position="calculatePosition"
        @close="removeEventsListeners"
        :selectable="() => !isDisabled"
        ref="vSelect"
      >
        <template #open-indicator="{ attributes }">
          <span v-bind="attributes">
            <span class="caret" />
          </span>
        </template>

        <template #no-options>
          <span v-html="noOptionsText" />
        </template>

        <template #option="{ label: optionLabel, value: optionValue }">
          <div class="option">
            <span v-html="optionLabel" />
            <i
              class="icon-check-o"
              v-if="selectedIcon(optionValue, value)"
            />
          </div>
        </template>
      </vSelect>
    </div>
    <Tooltip
      v-if="tooltip"
      :text="tooltip"
      :is-disabled="isDisabled"
    />
  </div>
</template>

<script>
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import Tooltip from '../Tooltip'

export default {
  name: 'FormSelect',
  components: {
    vSelect,
    Tooltip
  },
  props: {
    options: {
      type: Array,
      required: true
    },
    value: {
      type: [String, Object, null],
      default: null
    },
    label: {
      type: String,
      default: undefined
    },
    reduce: {
      type: Function,
      required: false,
      default: undefined
    },
    selectedIcon: {
      type: Function,
      default: (optionVal, currentVal) => optionVal === currentVal
    },
    placeholder: {
      type: String,
      required: false,
      default: ''
    },
    noOptionsText: {
      type: String,
      required: false,
      default: undefined
    },
    isSearchable: {
      type: Boolean,
      required: false,
      default: false
    },
    isDisabled: {
      type: Boolean,
      required: false,
      default: false
    },
    scrollableParent: {
      type: Object,
      default: undefined
    },
    tooltip: {
      type: String,
      default: undefined
    },
    ariaLabel: {
      type: String,
      default: undefined
    }
  },
  mounted () {
    this.setAriaLabel()
    this.setAriaDisabled()
  },
  unmounted () {
    this.removeEventsListeners()
  },
  computed: {
    scrollableParentForListeners () {
      return this.scrollableParent ?? window
    }
  },
  methods: {
    onInput (e) {
      this.$emit('onInput', e)
    },
    onSearch (search, loading) {
      this.$emit('onSearch', { search, loading })
    },
    calculatePosition (dropdownList, component, { left, top }) {
      const mainVueSelectStyle = window.getComputedStyle(document.querySelector('div.m-vue-select'))
      const parsedLeft = parseInt(left)
      const viewportWidth = window.innerWidth
      const rightPadding = 10
      const minWidth = 250
      const maxWidth = viewportWidth - parsedLeft - rightPadding

      dropdownList.classList.add('m-vue-select')
      if (this.isDisabled) dropdownList.classList.add('m-vue-select--disabled')
      dropdownList.style.top = top
      dropdownList.style.left = left
      dropdownList.style.fontFamily = mainVueSelectStyle.fontFamily
      dropdownList.style.width = 'auto'
      dropdownList.style.overflow = 'hidden'
      dropdownList.style.maxWidth = `${maxWidth}px`
      dropdownList.style.minWidth = `${Math.min(maxWidth, minWidth)}px`

      this.addEventsListeners()
    },
    forceClose () {
      if (this.$refs.vSelect) this.$refs.vSelect.searchEl.blur()
    },
    addEventsListeners () {
      window.addEventListener('resize', this.forceClose)
      this.scrollableParentForListeners.addEventListener('scroll', this.forceClose)
    },
    removeEventsListeners () {
      window.removeEventListener('resize', this.forceClose)
      this.scrollableParentForListeners.removeEventListener('scroll', this.forceClose)
    },
    getComboBoxDiv () {
      return this.$refs.vSelectContainer.querySelector('[role="combobox"][aria-owns]')
    },
    setAriaLabel () {
      if (this.ariaLabel) {
        const comboboxDiv = this.getComboBoxDiv()
        if (comboboxDiv) comboboxDiv.ariaLabel = this.ariaLabel
      }
    },
    setAriaDisabled () {
      const comboboxDiv = this.getComboBoxDiv()
      if (comboboxDiv) comboboxDiv.ariaDisabled = this.isDisabled.toString()
    }
  },
  watch: {
    ariaLabel () {
      this.setAriaLabel()
    },
    isDisabled () {
      this.setAriaDisabled()
    }
  }
}
</script>

<style lang="less">
@import (reference) "./../../../less/shared/utilities.less";
@import "@{frontend_static_less}/public/modules/m-vue-select.less";

.m-vue-select-container {
  display: flex;
  white-space: nowrap;
}

.m-vue-select--disabled {
  pointer-events: none;

  .m-vue-select, .m-vue-select-label, .option {
    filter: grayscale() opacity(40%);
  }
}

.m-vue-select-label {
  margin-right: 0.25rem;
}
</style>
